import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import IntroCarousel, { introSwipeTransition, introSwipeVariants } from "./IntroCarousel";
import LoginStreak from "./LoginStreak";
import { INTRO_SLIDES } from "./introData";
import { markIntroSeen } from "./introStorage";
import { AuthContext } from "../../apiCalls/jwt/AuthContext";

export { INTRO_COMPLETE_KEY, hasSeenIntro } from "./introStorage";

function useScreenHeight() {
  const [height, setHeight] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerHeight,
  );

  useEffect(() => {
    const update = () => {
      const next = window.visualViewport?.height || window.innerHeight;
      setHeight(Math.round(next));
    };
    update();
    window.visualViewport?.addEventListener("resize", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.visualViewport?.removeEventListener("resize", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return height;
}

const Shell = ({ children, height, lockHeight }) => (
  <Box
    sx={{
      width: "100%",
      height: lockHeight ? height : "auto",
      minHeight: lockHeight ? height : "100vh",
      maxHeight: lockHeight ? height : "none",
      background: "#0D0F0A",
      display: "flex",
      alignItems: "stretch",
      justifyContent: "center",
      overflowX: "hidden",
      overflowY: lockHeight ? "hidden" : "auto",
    }}
  >
    {children}
  </Box>
);

function completeIntro(navigate, location) {
  markIntroSeen();
  if (location.state?.fromProfileMenu) {
      navigate("/", { replace: true, state: { openProfileMenu: true } });
  } else {
      navigate("/", { replace: true });
  }
}

function AppIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useContext(AuthContext);
  const isDesktop = useMediaQuery("(min-width:900px)");
  const screenHeight = useScreenHeight();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const isAuthenticated = Boolean(auth?.accessToken);

  useEffect(() => {
    // Guests should never see intro / streak — send them home.
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    markIntroSeen();
  }, [isAuthenticated, navigate]);

  const finish = () => completeIntro(navigate, location);
  const goTo = (next) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };
  const panelKey = step <= 3 ? "intro-slides" : step;

  return (
    <Shell height={screenHeight || "100svh"} lockHeight={!isDesktop}>
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={panelKey}
          custom={direction}
          variants={introSwipeVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={introSwipeTransition}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {step <= 3 && (
            <IntroCarousel
              slide={INTRO_SLIDES[step]}
              direction={direction}
              onBack={() => {
                if (step === 0) finish();
                else goTo(step - 1);
              }}
              onSkip={() => goTo(4)}
              onContinue={() => goTo(step + 1)}
            />
          )}
          {step === 4 && (
            <LoginStreak
              onClaim={(claimed) => {
                const today = new Date().toISOString().slice(0, 10);
                if (claimed) {
                  sessionStorage.setItem(`ga_streak_shown_${today}`, "1");
                  sessionStorage.setItem(`ga_streak_claimed_${today}`, "1");
                }
                finish();
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </Shell>
  );
}

export default AppIntro;
